import { useNetwork } from "@vueuse/core";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import { computed, ref, watch } from "vue";

import { notify } from "~/components/notifications";
import { type ApiResult, type Transaction, type TransactionResponse, TransactionsService } from "~/shared/common";
import { NotificationType, TransactionKind } from "~/shared/enums";
import type { Operation, StreamCallback } from "~/shared/types";
import { useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { isObject, makeDuid } from "~/utils/common";
import { printOrCaptureException } from "~/utils/exceptionClient";
import { lsGet, lsRemove, lsSet, REQUEST_QUEUE_KEY } from "~/utils/localStorageManager";
import { timeout } from "~/utils/wait";

import useAbortInterceptor from "./useAbortInterceptor";

// TODO transactions need to be clarified. the really big ones obviously can't be done in one go on the BE, but we should still be able to undo/redo somehow
export const MAX_OPERATIONS_PER_REQUEST = 50;

export enum RequestKind {
  TRANSACTION,
  GET,
  PUT,
  POST,
}
type RequestOptions = {
  nonBlocking?: boolean;
  background?: boolean;
  abortPreviousRequest?: boolean;
  key?: string;
};
type Request = RequestOptions &
  (
    | {
        kind: RequestKind.TRANSACTION;
        transactions: Transaction[];
      }
    | {
        kind: RequestKind.GET;
        duid: string;
        url: string;
      }
    | {
        kind: RequestKind.POST | RequestKind.PUT;
        duid: string;
        url: string;
        data?: object;
        headers?: Record<string, string>;
        onStream?: StreamCallback;
      }
  );
type BackendOldError = { response: { status: number; data: { errors: string[] | undefined } } };

const network = useNetwork();

// Capture errors and send to sentry
axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (
      !!error &&
      error.name !== "AbortError" &&
      !axios.isCancel(error) &&
      (error.response ? error.response.status !== 404 : true)
    ) {
      printOrCaptureException(error);
    }
    throw error;
  }
);

// Add abort interceptor
const { abortRequest } = useAbortInterceptor(axios);

const randomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

const callWithErrorHandling = async <T>(
  fn: () => Promise<T>,
  totalTries: number = 1, // TODO put back to 3 when the BE is better
  backoff: number = 2000
): Promise<[T, undefined] | [undefined, unknown]> => {
  for (let attempt = 0; attempt < totalTries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return [await fn(), undefined];
    } catch (error) {
      const errorResponseOrig = error as ApiResult | BackendOldError;
      const errorResponse = "response" in errorResponseOrig ? errorResponseOrig.response : errorResponseOrig;
      const { status } = errorResponse;

      if (axios.isCancel(error)) {
        return [undefined, error];
      }
      if (status === 401) {
        // The user is not logged in, log out
        useUserStore().forceLogout();
        return [undefined, error];
      }
      if (status === 403) {
        // It's a CSRF error, reset and try again once
        usePageStore().updateCsrf();
        try {
          // eslint-disable-next-line no-await-in-loop
          return [await fn(), undefined];
        } catch (innerError) {
          return [undefined, innerError];
        }
      }
      if (status < 500 && status !== 429) {
        // It's not a server error, don't retry
        return [undefined, error];
      }

      if (attempt === totalTries - 1) {
        return [undefined, error];
      }

      const jitter = randomInt(0, backoff);
      const delayTime = backoff * 2 ** attempt + jitter;
      // eslint-disable-next-line no-await-in-loop
      await timeout(delayTime);
    }
  }
  throw new Error("Failed to call with error handling");
};

class RequestManager {
  #isOnline = ref(false);

  #queue = ref<Request[]>([]);

  #pending = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #idToFnMap = new Map<string, [(value: unknown) => void, (reason?: any) => void]>();

  working = computed(() => this.#queue.value.filter((e) => !e.nonBlocking).length > 0);

  constructor() {
    this.#loadQueue();
  }

  async init() {
    const environmentStore = useEnvironmentStore();

    this.#isOnline = computed(() => network.isOnline.value || environmentStore.isLocal);

    watch(
      () => this.#isOnline.value,
      (newIsOnline) => {
        if (!newIsOnline) {
          this.#saveQueue();
          return;
        }

        this.#loadQueue();
        this.#send();
      }
    );

    this.#send();
  }

  #loadQueue() {
    const requestQueueStr = lsGet(REQUEST_QUEUE_KEY);
    if (!requestQueueStr) {
      return;
    }

    lsRemove(REQUEST_QUEUE_KEY);
    this.#queue.value = JSON.parse(requestQueueStr);
  }

  #saveQueue() {
    this.#queue.value.forEach((request) => {
      if (request.kind !== RequestKind.POST && request.kind !== RequestKind.PUT) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      delete request.onStream;
    });

    const oldQueue = JSON.parse(lsGet(REQUEST_QUEUE_KEY) ?? "[]");
    lsSet(REQUEST_QUEUE_KEY, JSON.stringify([...oldQueue, ...this.#queue.value]));
    this.#queue.value = [];
  }

  #makePromise(duid: string) {
    return new Promise((resolve, reject) => {
      this.#idToFnMap.set(duid, [resolve, reject]);
    });
  }

  #resolvePromise(duid: string, value: unknown, error: unknown, shouldNotify: boolean) {
    const resolveAndReject = this.#idToFnMap.get(duid);
    if (!resolveAndReject) {
      return;
    }

    const [resolve, reject] = resolveAndReject;
    if (error) {
      if (shouldNotify && !axios.isCancel(error)) {
        const title = error instanceof Error ? error.message : error.toString();
        const detail = isObject(error) && isObject(error.body) ? ` ${JSON.stringify(error.body)}` : "";
        notify({
          message: `Request failed: ${title}${detail}`,
          type: NotificationType.ERROR,
        });
      }
      reject(error);
      return;
    }

    resolve(value);
  }

  async #sendRequest(request: Request) {
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();

    if (tenantStore.isDart && pageStore.networkDelay) {
      await timeout(randomInt(3000, 5000));
    }

    const shouldNotify = environmentStore.isLocal || tenantStore.isDart;
    const { kind, abortPreviousRequest, key } = request;
    switch (kind) {
      case RequestKind.TRANSACTION: {
        const { transactions } = request;
        const [responseBody, error] = await callWithErrorHandling(() =>
          TransactionsService.transactionsCreate(
            {
              items: transactions,
              clientDuid: pageStore.duid,
            },
            pageStore.csrftoken
          )
        );
        const duidToResult = new Map(
          responseBody?.results?.map((transactionResponse) => [transactionResponse.duid, transactionResponse])
        );
        transactions.forEach(({ duid }) => {
          this.#resolvePromise(duid, duidToResult.get(duid), error, shouldNotify);
        });
        break;
      }
      case RequestKind.GET: {
        const { duid, url } = request;
        const [response, error] = await callWithErrorHandling(() => axios.get(url, { abortPreviousRequest, key }));
        this.#resolvePromise(duid, response, error, shouldNotify);
        break;
      }
      case RequestKind.PUT: {
        const { duid, url, data, headers, onStream } = request;
        const [response, error] = await callWithErrorHandling(() =>
          axios.put(url, data, {
            headers: { ...headers },
            onUploadProgress: onStream,
            key,
            abortPreviousRequest,
          })
        );
        this.#resolvePromise(duid, response, error, shouldNotify);
        break;
      }
      case RequestKind.POST: {
        const { duid, url, data, headers, onStream } = request;
        const [response, error] = await callWithErrorHandling(() =>
          axios.post(url, data, {
            headers: { ...headers, "x-csrftoken": pageStore.csrftoken ?? "", "client-duid": pageStore.duid },
            onDownloadProgress: onStream,
            abortPreviousRequest,
            key,
          })
        );
        this.#resolvePromise(duid, response, error, shouldNotify);
        break;
      }
      default: {
        throw new Error(`Unknown request kind: ${kind}`);
      }
    }
  }

  async #send() {
    if (this.#pending || !this.#isOnline.value) {
      return;
    }
    this.#pending = true;

    try {
      while (this.#queue.value.length > 0) {
        const first = this.#queue.value[0];
        if ("background" in first && first.background) {
          // Don't wait for background requests
          this.#sendRequest(first);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await this.#sendRequest(first);
        }
        this.#queue.value.shift();
      }
    } finally {
      this.#pending = false;
    }
  }

  async transact(
    kind: TransactionKind,
    operations: Operation[],
    nonBlocking?: boolean,
    background?: boolean
  ): Promise<TransactionResponse[]> {
    const transactions = Array.from({ length: Math.ceil(operations.length / MAX_OPERATIONS_PER_REQUEST) }, (_, i) => ({
      duid: makeDuid(),
      kind,
      operations: operations.slice(i * MAX_OPERATIONS_PER_REQUEST, (i + 1) * MAX_OPERATIONS_PER_REQUEST),
      nonBlocking,
    }));

    const addToQueue = () => {
      const last = this.#queue.value[this.#queue.value.length - 1];
      if (
        last &&
        last.kind === RequestKind.TRANSACTION &&
        last.background === background &&
        last.nonBlocking === nonBlocking &&
        !(this.#queue.value.length === 1 && this.#pending) &&
        last.transactions.reduce((acc, e) => acc + e.operations.length, 0) + operations.length <
          MAX_OPERATIONS_PER_REQUEST
      ) {
        last.transactions.push(transactions[0]);
      } else {
        this.#queue.value.push(
          ...transactions.map(
            (e) => ({ kind: RequestKind.TRANSACTION, transactions: [e], background }) satisfies Request
          )
        );
      }
    };

    if (!this.#isOnline.value) {
      this.#loadQueue();
      addToQueue();
      this.#saveQueue();
      return undefined as unknown as TransactionResponse[];
    }

    const promises = transactions.map(
      (transaction) => this.#makePromise(transaction.duid) as Promise<TransactionResponse>
    );

    if (background) {
      // Execute the background request immediately, so that it isn't blocked by the queue
      transactions.forEach((transaction) =>
        this.#sendRequest({ kind: RequestKind.TRANSACTION, transactions: [transaction], background })
      );
    } else {
      addToQueue();
      this.#send();
    }

    return Promise.all(promises);
  }

  async get(url: string, options?: RequestOptions) {
    const duid = makeDuid();
    const request: Request = {
      kind: RequestKind.GET,
      duid,
      url,
      ...options,
    };

    if (!this.#isOnline.value) {
      this.#loadQueue();
      this.#queue.value.push(request);
      this.#saveQueue();
      return undefined as unknown as AxiosResponse;
    }

    const promise = this.#makePromise(duid) as Promise<AxiosResponse>;

    if (options?.background) {
      // Execute the background request immediately, so that it isn't blocked by the queue
      this.#sendRequest(request);
    } else {
      this.#queue.value.push(request);
      this.#send();
    }

    return promise;
  }

  async put(
    url: string,
    data?: object,
    headers?: Record<string, string>,
    onStream?: StreamCallback,
    options?: RequestOptions
  ) {
    const duid = makeDuid();
    const request: Request = {
      kind: RequestKind.PUT,
      duid,
      url,
      data,
      headers,
      onStream,
      nonBlocking: options?.nonBlocking,
      background: options?.background,
      abortPreviousRequest: options?.abortPreviousRequest,
      key: options?.key,
    };

    if (!this.#isOnline.value) {
      this.#loadQueue();
      this.#queue.value.push(request);
      this.#saveQueue();
      return undefined as unknown as AxiosResponse;
    }

    const promise = this.#makePromise(duid) as Promise<AxiosResponse>;

    if (options?.background) {
      // Execute the background request immediately, so that it isn't blocked by the queue
      this.#sendRequest(request);
    } else {
      this.#queue.value.push(request);
      this.#send();
    }

    return promise;
  }

  async post(
    url: string,
    data?: object,
    headers?: Record<string, string>,
    onStream?: StreamCallback,
    options?: RequestOptions
  ) {
    const duid = makeDuid();
    const request: Request = {
      kind: RequestKind.POST,
      duid,
      url,
      data,
      headers,
      onStream,
      nonBlocking: options?.nonBlocking,
      background: options?.background,
      abortPreviousRequest: options?.abortPreviousRequest,
      key: options?.key,
    };

    if (!this.#isOnline.value) {
      this.#loadQueue();
      this.#queue.value.push(request);
      this.#saveQueue();
      return undefined as unknown as AxiosResponse;
    }

    const promise = this.#makePromise(duid) as Promise<AxiosResponse>;

    if (options?.background) {
      // Execute the background request immediately, so that it isn't blocked by the queue
      this.#sendRequest(request);
    } else {
      this.#queue.value.push(request);
      this.#send();
    }

    return promise;
  }
}

export const requestManager = new RequestManager();
export { abortRequest };
